import React, { useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import Search from "../../../assets/images/search.svg";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import { getPlatesByStatus } from "../../../services/platesService";
import {
  flattenProfilesByPlateStatus,
  getPlateDisplayImageUrl,
  getPlateLocationCountry,
  getPlateLocationState,
  getPlateNumber,
  getProfileDisplayName,
  getProfileEmail,
  getProfileUserImage,
  mapPlateStatusLabel,
} from "../../../utils/platesProfileHelpers";
import "../PendingPlatesClaims/PendingPlatesClaims.scss";

const STATUS_LIST = ["pending", "approved", "reported"];

function PlatesManagement() {
  const [searchTerm, setSearchTerm] = useState("");

  const statusQueries = useQueries({
    queries: STATUS_LIST.map((status) => ({
      queryKey: ["plates", status],
      queryFn: () => getPlatesByStatus(status),
      retry: 1,
    })),
  });

  const isLoading = statusQueries.some((query) => query.isLoading);
  const isError = statusQueries.some((query) => query.isError);
  const firstError = statusQueries.find((query) => query.error)?.error;

  const transformedData = useMemo(() => {
    const formatDate = (dateString) => {
      if (!dateString) return { date: "N/A", time: "" };
      try {
        const date = new Date(dateString);
        const dateStr = date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
        const timeStr = date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        return { date: dateStr, time: timeStr };
      } catch (e) {
        return { date: dateString, time: "" };
      }
    };

    return statusQueries.flatMap((query, index) => {
      const status = STATUS_LIST[index];
      const statusData =
        query.data?.data || query.data?.plates || query.data || [];
      const flatRows = flattenProfilesByPlateStatus(statusData, status);

      return flatRows.map(({ profile, plateEntry, plateId }) => {
        const dateInfo = formatDate(
          profile.createdAt || profile.submittedAt || profile.timestamp,
        );

        const userName = getProfileDisplayName(profile);
        const userEmail = getProfileEmail(profile);
        const userImage = getProfileUserImage(profile);
        const plateImage =
          getPlateDisplayImageUrl(plateEntry, profile) ||
          process.env.PUBLIC_URL + "/images/uploadedImg.svg";
        const city =
          getPlateLocationState(plateEntry, profile) ||
          profile.location?.city ||
          "N/A";
        const country =
          getPlateLocationCountry(plateEntry, profile) ||
          profile.location?.country ||
          "N/A";

        return {
          id: plateId,
          image: userImage,
          name: userName,
          email: userEmail,
          claimId: plateId,
          plateNumber: getPlateNumber(plateEntry, profile),
          uploadImg: plateImage,
          datestamp: dateInfo.date,
          timestamp: dateInfo.time,
          city,
          country,
          status: mapPlateStatusLabel(plateEntry?.status || status),
          statusClass: (plateEntry?.status || status).toLowerCase(),
          originalData: { ...profile, plateEntry },
        };
      });
    });
  }, [statusQueries]);

  const filteredData = useMemo(() => {
    if (!searchTerm) return transformedData;
    const searchLower = searchTerm.toLowerCase();
    return transformedData.filter(
      (plate) =>
        plate.name.toLowerCase().includes(searchLower) ||
        plate.email.toLowerCase().includes(searchLower) ||
        plate.plateNumber.toLowerCase().includes(searchLower) ||
        plate.claimId.toLowerCase().includes(searchLower) ||
        plate.status.toLowerCase().includes(searchLower),
    );
  }, [transformedData, searchTerm]);

  const columns = [
    {
      name: "User ID",
      sortable: true,
      grow: 3,
      cell: (row) => (
        <div className="user-cell">
          <img src={row.image} alt="user" className="user-img" />
          <div>
            <strong>{row.name}</strong>
            <br />
            <span>{row.email}</span>
          </div>
        </div>
      ),
    },
    {
      name: "Claim ID",
      selector: (row) => row.claimId,
      sortable: true,
    },
    {
      name: "Plate Number",
      selector: (row) => row.plateNumber,
      sortable: true,
    },
    {
      name: "Uploaded Image",
      sortable: true,
      cell: (row) => (
        <div className="user-cell">
          <div className="upload-img">
            <img src={row.uploadImg} alt="uploadImg" />
          </div>
        </div>
      ),
    },
    {
      name: "Timestamp",
      sortable: true,
      grow: 1.5,
      cell: (row) => (
        <div className="timestamp">
          <strong>{row.datestamp}</strong>
          <br />
          <span>{row.timestamp}</span>
        </div>
      ),
    },
    {
      name: "Location",
      sortable: false,
      cell: (row) => (
        <div className="timestamp">
          <strong>{row.city}</strong>
          <br />
          <span>{row.country}</span>
        </div>
      ),
    },
    // {
    //   name: "Status",
    //   sortable: true,
    //   cell: (row) => (
    //     <span className={`status-badge ${row.statusClass}`}>
    //       <span className="dot"></span>
    //       {row.status}
    //     </span>
    //   ),
    // },
    {
      name: "",
      button: true,
      cell: (row) => (
        <div className="actions">
          <Link
            to="/plates-management/view"
            state={{ plateData: row.originalData }}
          >
            <img
              src={process.env.PUBLIC_URL + "/images/edit.svg"}
              className="me-2"
              alt="edit"
            />
          </Link>
        </div>
      ),
    },
  ];

  const customStyles = {
    table: { style: { backgroundColor: "transparent", color: "#0D0D0D" } },
    headRow: {
      style: {
        backgroundColor: "transparent",
        color: "#0D0D0D",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
      },
    },
    headCells: {
      style: {
        fontSize: "10px",
        fontWeight: "500",
        color: "#0D0D0D",
        padding: "12px 10px",
        backgroundColor: "transparent",
      },
    },
    rows: {
      style: {
        backgroundColor: "transparent",
        color: "#0D0D0D",
        fontSize: "10px",
        minHeight: "60px",
        "&:hover": {
          backgroundColor: "#EAE3E0 !important",
          border: "none !important",
          outline: "none !important",
        },
      },
    },
  };

  return (
    <React.Fragment>
      <header className="claims-panel-header items-center gap-3 flex content-justify-between">
        <div className="left-side">
          <h4 className="title">Plates Management</h4>
        </div>
        <div className="right-side">
          <div className="search_bar">
            <img src={Search} alt="Search" />
            <input
              type="text"
              placeholder="Search for plates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </header>
      <div className="pendingPlatesClaims">
        <div className="card">
          <h4>All Plates</h4>
          {isLoading ? (
            <div className="loading-container">
              <div className="loader"></div>
              <p>Loading plates...</p>
            </div>
          ) : isError ? (
            <div className="error-container">
              <p>Error loading plates. Please try again.</p>
              {firstError && (
                <small>
                  {firstError?.response?.data?.message || firstError?.message}
                </small>
              )}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
              responsive
              customStyles={customStyles}
              noDataComponent="No plates found"
            />
          )}
        </div>
      </div>
    </React.Fragment>
  );
}

export default PlatesManagement;
